import { NeedDto, NeedsActionDto } from '@act/shared/data-transfer-objects';
import { Box, Button, FlexContainer, ResourceCard } from '@act/storybook';
import { Typography } from '@mui/material';
import {
  getUniqueResourceReferralsFromAction,
  mapResourceDetailsToResourceCardProps,
} from 'react-components/forms/cboResourceForm/mapToResourceDetails.util';
import { usePlacesAutocompleteEnabled } from 'react-components/places/usePlacesAutocompleteEnabled.hook';

import {
  ClockCounterClockwiseIcon,
  IdentificationBadgeIcon,
} from '@phosphor-icons/react';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import useAddActionDialog from '@act/react-components/dialog-forms/hooks/useAddActionDialog.hook';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hook';
import { userAppSelectors } from 'redux/slices/usersSlice.slice';
import { generateDatadogTrackingProps } from 'toolkit/environment/environment/userMonitoring.util';
import {
  StyledHistoryEntry,
  StyledHistoryEntryDescription,
  StyledHistoryEntrySubheader,
  SubheaderAndDescription,
  getHistoryEntryDateLine,
} from './HistoryDisplay';

interface ActionHistoryEntryProps {
  need: NeedDto;
  action: NeedsActionDto;
  detailMode?: boolean;
  editMode?: boolean;
}

const ActionHistoryEntry = ({
  need,
  action,
  detailMode,
  editMode,
}: ActionHistoryEntryProps) => {
  const userMap = userAppSelectors.useSelectUsersMap();
  const needMetadata = metaSliceSelectors.useSelectNeedsMetadata();
  const openAddActionDialog = useAddActionDialog();
  const activities = needMetadata?.data?.activities;
  const needIconOptions = { needIssueId: need.issueId, issues: needMetadata?.data?.issues };
  const [latestAction, restOfActions] = useMemo(() => {
    const allActions = [action, ...(action.subsequentActions || [])];
    allActions.sort((a, b) => b.createdAt - a.createdAt);
    return [allActions[0], detailMode ? allActions.slice(1) : []];
  }, [action, detailMode]);

  const placesAutocompleteEnabled = usePlacesAutocompleteEnabled();
  const uniqueResourceReferrals = useMemo(
    () => getUniqueResourceReferralsFromAction(action),
    [action]
  );

  function openEditActionDialog() {
    void openAddActionDialog({ need, action });
  }

  function getActionTitle(action: NeedsActionDto): string {
    const relevantAction = action?.subsequentActions?.length
      ? [...action?.subsequentActions]?.sort(
          (a, b) => b.createdAt - a.createdAt
        )[0]
      : action;

    return activities?.find(i => i.id === relevantAction?.activityId)?.label;
  }

  return (
    <StyledHistoryEntry
      header={
        <FlexContainer
          direction="row"
          alignItems="center"
          gap={1}
          height="auto"
          overflowAllVisible
        >
          <Typography variant="titleSmall">{getActionTitle(action)}</Typography>
          {!!action.subsequentActions?.length && (
            <Box
              display="flex"
              marginTop="-2px"
              marginBottom="-2px"
              widthMaxContent
            >
              <ClockCounterClockwiseIcon color="primary" size={16} />
            </Box>
          )}
        </FlexContainer>
      }
      content={
        <div>
          <StyledHistoryEntrySubheader>
            {getHistoryEntryDateLine(
              DateTime.fromISO(latestAction.date).toMillis(),
              userMap && userMap[latestAction.creatorId]?.name
            )}
          </StyledHistoryEntrySubheader>
          <StyledHistoryEntryDescription>
            {latestAction.description}
          </StyledHistoryEntryDescription>
          {uniqueResourceReferrals.length > 0 ? (
            placesAutocompleteEnabled ? (
              <StyledHistoryEntryDescription>
                <FlexContainer
                  direction="column"
                  height="auto"
                  gap={1}
                  overflowAllVisible
                  margin="8px 0 0 0"
                  width="100%"
                >
                  {uniqueResourceReferrals.map(referral => (
                    <ResourceCard
                      key={
                        referral.id ??
                        `legacy-${referral.resourceName}-${referral.createdAt ?? ''}`
                      }
                      {...mapResourceDetailsToResourceCardProps(
                        referral,
                        needIconOptions
                      )}
                    />
                  ))}
                </FlexContainer>
              </StyledHistoryEntryDescription>
            ) : (
              <StyledHistoryEntryDescription>
                <FlexContainer
                  direction="row"
                  height="auto"
                  overflowAllVisible
                  alignItems="center"
                  gap={1}
                  margin="8px 0 0 0"
                  width="100%"
                >
                  <IdentificationBadgeIcon size={16} />
                  <Typography variant="bodySmall" className="resource-count">
                    {uniqueResourceReferrals.length} resource
                    {uniqueResourceReferrals.length !== 1 ? 's' : ''} referred
                  </Typography>
                </FlexContainer>
              </StyledHistoryEntryDescription>
            )
          ) : null}
        </div>
      }
      expandedContent={
        !!restOfActions.length && (
          <>
            {restOfActions.map(a => (
              <SubheaderAndDescription
                key={a.id}
                subheader={getHistoryEntryDateLine(
                  DateTime.fromISO(a.date).toMillis(),
                  userMap && userMap[a.creatorId]?.name
                )}
                description={a.description}
              />
            ))}
          </>
        )
      }
      footer={
        editMode && (
          <Button
            variant="text"
            onClick={() => openEditActionDialog()}
            {...generateDatadogTrackingProps('need-action-entry-edit')}
          >
            Edit
          </Button>
        )
      }
    />
  );
};

export default ActionHistoryEntry;
